import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, DataTable, Layer, Pagination } from 'grommet';
import { Add } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { ChargingNetworkService, CreateChargingNetworkRequest } from '/src/api/services/ChargingNetworkService';
import { FooterNav, HeaderNav } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import {
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  LoadingSpinner,
  Text,
} from '/src/components/common';
import { Input, SearchInput } from '/src/components/forms';
import type { ChargingNetworks } from '/src/lib/models/ChargingNetwork';
import { toastMessages } from '/src/lib/toast';

const FuseAdminChargingNetworks = observer(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const [chargingNetworks, setChargingNetworks] = useState<ChargingNetworks[]>([]);
  const [filteredChargingNetworks, setFilteredChargingNetworks] = useState<ChargingNetworks[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [formData, setFormData] = useState<CreateChargingNetworkRequest>({
    name: '',
  });

  useEffect(() => {
    fetchChargingNetworks();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredChargingNetworks(chargingNetworks);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = chargingNetworks.filter((network) => network.name.toLowerCase().includes(query));
      setFilteredChargingNetworks(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, chargingNetworks]);

  const fetchChargingNetworks = async () => {
    try {
      setIsLoading(true);
      const networks = await ChargingNetworkService.listChargingNetworks();
      setChargingNetworks(networks);
      setFilteredChargingNetworks(networks);
    } catch (err) {
      console.error('Error fetching charging networks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const paginatedChargingNetworks = filteredChargingNetworks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNameError('Charging network name is required');
      toast.error(toastMessages.createChargingNetwork.requiredFields);
      return;
    }

    try {
      setIsSubmitting(true);
      await ChargingNetworkService.create(formData);
      toast.success(toastMessages.createChargingNetwork.success);
      setIsModalVisible(false);
      setFormData({
        name: '',
      });
      setNameError('');
      fetchChargingNetworks();
    } catch (error) {
      console.error('Error creating charging network:', error);
      toast.error(toastMessages.createChargingNetwork.error(error as ApiError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setFormData({
      name: '',
    });
    setNameError('');
  };

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Charging Networks
              </Text>
            </Box>
            <Box align="end">
              <Button
                primary
                icon={<Add />}
                label="Add Charging Network"
                onClick={() => setIsModalVisible(true)}
                style={{
                  borderRadius: '8px',
                  fontWeight: 'normal',
                }}
              />
            </Box>

            <Card margin={{ vertical: 'small' }} background="white" elevation="none" round="xsmall">
              <CardHeader title="Charging Network List">
                <Box flex="grow" align="end">
                  <SearchInput
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSearch={handleSearch}
                    placeholder="Search CNO/CMS..."
                  />
                </Box>
              </CardHeader>
              <CardBody pad="none">
                {isLoading ? (
                  <Box pad="large" align="center" justify="center" height="200px">
                    <LoadingSpinner />
                  </Box>
                ) : chargingNetworks.length === 0 ? (
                  <Box pad="large" align="center">
                    <Text>No charging networks found</Text>
                  </Box>
                ) : (
                  <>
                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'name',
                          header: <DataTableHeader toUpperCase>Charging Network Name</DataTableHeader>,
                          size: '70%',
                          render: (datum: ChargingNetworks) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.name}</DataTableItem>
                            </Box>
                          ),
                        },
                      ]}
                      data={paginatedChargingNetworks}
                      sortable
                      background={['light-6', 'white']}
                      border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                    />
                    {filteredChargingNetworks.length > itemsPerPage && (
                      <Box pad={{ horizontal: 'medium', vertical: 'small' }} align="center">
                        <Pagination
                          alignSelf="center"
                          size="small"
                          page={currentPage}
                          onChange={({ page }) => setCurrentPage(page)}
                          step={itemsPerPage}
                          numberItems={filteredChargingNetworks.length}
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>

      {isModalVisible && (
        <Layer
          position="center"
          onEsc={handleCloseModal}
          onClickOutside={handleCloseModal}
          responsive={false}
          style={{
            width: 'auto',
            maxWidth: 'min(95vw, 500px)',
            maxHeight: '95vh',
            overflow: 'auto',
            borderRadius: '8px',
          }}
        >
          <Card width="100%" style={{ width: '100%' }}>
            <CardHeader title="Add Charging Network" />
            <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
              <Box gap="medium">
                <Input
                  label="Charging Network Name"
                  value={formData.name}
                  setValue={(value) => {
                    setFormData({ ...formData, name: value });
                    if (nameError) setNameError('');
                  }}
                  placeholder="Enter charging network name"
                  required
                  error={nameError}
                  style={{ width: '100%' }}
                />

                <Box direction="row" gap="small" justify="end" margin={{ top: 'small' }}>
                  <Button
                    label="Cancel"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                  <Button
                    primary
                    label={isSubmitting ? 'Creating...' : 'Create Charging Network'}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                </Box>
              </Box>
            </CardBody>
          </Card>
        </Layer>
      )}
    </Box>
  );
});

export default FuseAdminChargingNetworks;
